import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { calendarApi, getCalendarToken, clearCalendarToken } from './calendarApi';
import { formatWelcomeBanner } from './calendarWelcomeText';
import CalendarLogin from './CalendarLogin';
import CalendarHome from './CalendarHome';
import CalendarState from './CalendarState';
import CalendarCounty from './CalendarCounty';
import CalendarMonth from './CalendarMonth';
import CalendarUnsubscribe from './CalendarUnsubscribe';
import CalendarSubscribe from './CalendarSubscribe';
import CalendarAccount from './CalendarAccount';
import { CalendarForgotPassword, CalendarResetPassword, CalendarResubscribe } from './CalendarAuthPages';
import { CalendarTerms, CalendarPrivacy } from './CalendarLegal';

/**
 * The subscriber calendar, whole.
 *
 * Mounted by App.js when the hostname is calendar.yourfirstparcel.com, and
 * rendered alone: no tenant navigation, no marketing navigation, no tenant
 * config. It is inside the Router that App.js already provides, so it declares
 * Routes and not another BrowserRouter.
 *
 * MOBILE FIRST, and every colour a theme token rather than a literal. That is
 * how the master admin calendar already gets dark mode with no second
 * stylesheet, and it is why there is no media query in here for colour: the
 * tokens change, the layout does not.
 *
 * THREE AUDIENCES, NOT ONE. Task 4 turned this from "signed in or not" into
 * three states that each need a different door:
 *
 *   no session       the public pages: subscribe, sign in, reset a password,
 *                    and the legal pages, which must be readable by somebody
 *                    deciding whether to buy at all.
 *   paying           the calendar.
 *   not paying       signed in, but past_due or cancelled. They reach their
 *                    account page and nothing else, because the account page is
 *                    where the thing that is wrong gets fixed. Turning them away
 *                    at the login screen left the two states we most want people
 *                    to be able to fix as the two they could not sign in to fix.
 */

export const REPORT_EMAIL_NOTE = 'Tell us what looks wrong and we will check the county source.';

/**
 * The confirmation a new subscriber lands on.
 *
 * Dismissable and held in memory only, so it is shown once for the signup that
 * produced it and never again: a reload does not bring it back, because a
 * reload is not another payment.
 */
function WelcomeBanner({ text, onDismiss }) {
  return (
    <div
      role="status"
      style={{
        maxWidth: '900px',
        margin: '16px auto 0',
        padding: '12px 16px',
        border: '1px solid var(--color-primary)',
        borderRadius: '8px',
        background: 'var(--color-surface)',
        color: 'var(--color-text)',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}
    >
      <span style={{ flex: 1 }}>{text}</span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          fontSize: '1rem',
          lineHeight: 1,
          padding: '2px 4px'
        }}
      >
        x
      </button>
    </div>
  );
}

/** Routes that must work with no session at all. */
const PUBLIC_PREFIXES = [
  '/unsubscribe/',
  '/subscribe',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/privacy'
];

function isPublicPath(pathname) {
  return PUBLIC_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(prefix));
}

function shellStyle() {
  return {
    minHeight: '100vh',
    background: 'var(--color-bg)',
    color: 'var(--color-text)'
  };
}

function CalendarNav({ subscriber, access, onSignOut }) {
  const location = useLocation();
  const linkStyle = (path) => ({
    color: location.pathname === path ? 'var(--color-primary)' : 'var(--color-text-light)',
    textDecoration: 'none',
    fontWeight: location.pathname === path ? 700 : 500,
    padding: '10px 4px',
    fontSize: '0.95rem'
  });

  const canRead = !access || access.calendar;

  return (
    <header style={{
      borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-surface)',
      position: 'sticky',
      top: 0,
      zIndex: 10
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '10px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center'
      }}>
        <Link to={canRead ? '/' : '/account'} style={{
          fontWeight: 800,
          fontSize: '1.05rem',
          color: 'var(--color-heading)',
          textDecoration: 'none',
          marginRight: 'auto'
        }}>
          Auction Calendar
        </Link>
        <nav style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          {canRead && <Link to="/" style={linkStyle('/')}>Upcoming</Link>}
          {canRead && <Link to="/month" style={linkStyle('/month')}>Month</Link>}
          <Link to="/account" style={linkStyle('/account')}>Account</Link>
          {subscriber && (
            <button
              onClick={onSignOut}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                padding: '10px 0'
              }}
            >
              Sign out
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

function CalendarApp() {
  const [subscriber, setSubscriber] = useState(null);
  const [access, setAccess] = useState(null);
  const [liveStates, setLiveStates] = useState([]);
  const [comingSoonStates, setComingSoonStates] = useState([]);
  const [checking, setChecking] = useState(true);
  const [welcome, setWelcome] = useState('');
  const [finishingSignup, setFinishingSignup] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const publicRoute = isPublicPath(location.pathname);

  const loadMe = useCallback(async () => {
    if (!getCalendarToken()) {
      setChecking(false);
      return;
    }
    try {
      const data = await calendarApi('/me');
      setSubscriber(data.subscriber);
      setAccess(data.access || null);
      setLiveStates(data.liveStates || []);
      setComingSoonStates(data.comingSoonStates || []);
    } catch {
      // calendarApi already cleared the token on a 401. Falling through to the
      // login screen is the correct outcome, not an error worth showing.
      setSubscriber(null);
      setAccess(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  /**
   * Everything that happens after a card is charged, in one place.
   *
   * THE ORDER MATTERS AND IT IS NOT THE OBVIOUS ONE. Navigating before the
   * session reload finishes is what avoids two wrong screens: the subscribe
   * form rendering once more inside the signed-in shell, and the login screen
   * flashing at a path that needs a session the app has a token for but has not
   * read yet. finishingSignup holds the loading screen over that gap, rather
   * than reusing `checking`, which would make every account page save flash the
   * whole app.
   *
   * replace: true so that Back does not return to a subscribe form for a
   * subscription that now exists.
   */
  const handleSubscribed = useCallback(async (newSubscriber) => {
    setWelcome(formatWelcomeBanner(newSubscriber));
    setFinishingSignup(true);
    navigate('/', { replace: true });
    try {
      await loadMe();
    } finally {
      setFinishingSignup(false);
    }
  }, [loadMe, navigate]);

  /**
   * Everything that happens after a password is accepted, in one place.
   *
   * Same shape as handleSubscribed above and for the same reason: this
   * component owns the router, so the form does not.
   *
   * THE NAVIGATION IS WHAT THIS ADDS. A subscriber who can read the calendar
   * lands on it, which is where they already were. One who cannot is sent to
   * the page where their problem is solvable: the account page for a card that
   * failed, the resubscribe page for a subscription that ended. The verdict and
   * the destination both come from the server, so the browser is not deciding
   * who may read what.
   *
   * The catch-all route below would eventually redirect an unreadable session
   * anyway. Doing it here as well is not redundant: it makes the landing an
   * explicit consequence of signing in rather than a side effect of a route
   * failing to match, and it happens before the first paint of a shell the
   * subscriber has no business seeing.
   */
  const handleSignedIn = useCallback(async (session) => {
    await loadMe();
    const redirect = session && session.access && session.access.redirect;
    if (redirect) navigate(redirect, { replace: true });
  }, [loadMe, navigate]);

  const signOut = () => {
    clearCalendarToken();
    setSubscriber(null);
    setAccess(null);
    setWelcome('');
  };

  // The public pages render before any session check. They have to work for
  // somebody who has never had an account, and the legal pages have to be
  // readable by somebody deciding whether to buy one.
  if (publicRoute && !subscriber && !finishingSignup) {
    return (
      <div style={shellStyle()}>
        <Routes>
          <Route path="/unsubscribe/:token" element={<CalendarUnsubscribe />} />
          <Route path="/subscribe" element={<CalendarSubscribe onSubscribed={handleSubscribed} />} />
          <Route path="/forgot-password" element={<CalendarForgotPassword />} />
          <Route path="/reset-password" element={<CalendarResetPassword />} />
          <Route path="/terms" element={<CalendarTerms />} />
          <Route path="/privacy" element={<CalendarPrivacy />} />
        </Routes>
      </div>
    );
  }

  if (checking || finishingSignup) {
    return (
      <div style={{ ...shellStyle(), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>
          {finishingSignup ? 'Setting up your calendar' : 'Loading your calendar'}
        </p>
      </div>
    );
  }

  if (!subscriber) {
    return (
      <div style={shellStyle()}>
        <CalendarLogin onSignedIn={handleSignedIn} />
      </div>
    );
  }

  const canRead = !access || access.calendar;

  return (
    <div style={shellStyle()}>
      <CalendarNav subscriber={subscriber} access={access} onSignOut={signOut} />
      {welcome && <WelcomeBanner text={welcome} onDismiss={() => setWelcome('')} />}
      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '16px' }}>
        <Routes>
          <Route path="/terms" element={<CalendarTerms />} />
          <Route path="/privacy" element={<CalendarPrivacy />} />
          <Route
            path="/account"
            element={<CalendarAccount liveStates={liveStates} onChanged={loadMe} />}
          />
          <Route path="/resubscribe" element={<CalendarResubscribe subscriber={subscriber} />} />

          {/*
            The signed-in copy of the subscribe form, which is what a cancelled
            subscriber reaches from /resubscribe. Their address is filled in
            because we know it: they are signed in, it is the row the new
            subscription attaches to, and retyping it is both a nuisance and a
            way to end up with a second account.

            Only in this branch. The signed-out copy above takes no initial
            email, because there is no session there to take one from, and an
            address arriving from anywhere else is somebody else's.
          */}
          <Route
            path="/subscribe"
            element={(
              <CalendarSubscribe
                onSubscribed={handleSubscribed}
                initialEmail={subscriber.email}
              />
            )}
          />

          {/*
            The calendar itself, and only while it is being paid for. A
            subscriber who is past_due or cancelled is sent where the problem
            can actually be solved rather than to an error.
          */}
          {canRead ? (
            <>
              <Route
                path="/"
                element={<CalendarHome liveStates={liveStates} comingSoonStates={comingSoonStates} />}
              />
              <Route path="/month" element={<CalendarMonth />} />
              <Route path="/states/:code" element={<CalendarState />} />
              <Route path="/counties/:id" element={<CalendarCounty />} />
            </>
          ) : (
            <Route
              path="*"
              element={<Navigate to={(access && access.redirect) || '/account'} replace />}
            />
          )}

          <Route path="*" element={<Navigate to={canRead ? '/' : '/account'} replace />} />
        </Routes>
      </main>
      <footer style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '24px 16px 40px',
        color: 'var(--color-text-muted)',
        fontSize: '0.8rem'
      }}>
        <p style={{ margin: '0 0 8px' }}>
          Auction Calendar by Your First Parcel. Dates and lists change without notice,
          so always confirm with the county before you rely on one.
        </p>
        <p style={{ margin: 0 }}>
          <Link to="/terms" style={{ color: 'var(--color-text-muted)' }}>Terms</Link>
          <span style={{ padding: '0 8px' }}>·</span>
          <Link to="/privacy" style={{ color: 'var(--color-text-muted)' }}>Privacy</Link>
        </p>
      </footer>
    </div>
  );
}

export default CalendarApp;
